import apiClient from './client';
import type {
  UserAccountDetail,
  UserAccountStats,
  UserAccountFilters,
  PaginatedUserAccounts,
  UserPatchPayload,
  SubscriptionPayload,
  StripeLiveStats,
  UserSubscription,
} from '../types/userAccount';

const BASE = '/admin/users';

export async function getUsers(filters: UserAccountFilters = {}): Promise<PaginatedUserAccounts> {
  const params: Record<string, string | number | boolean> = {};
  if (filters.search) params.search = filters.search;
  if (filters.plan) params.plan = filters.plan;
  if (filters.status) params.status = filters.status;
  if (filters.ordering) params.ordering = filters.ordering;
  if (filters.page) params.page = filters.page;
  if (filters.page_size) params.page_size = filters.page_size;

  const { data } = await apiClient.get<PaginatedUserAccounts>(`${BASE}/`, { params });
  return data;
}

export async function getUserDetail(userId: number): Promise<UserAccountDetail> {
  const { data } = await apiClient.get<UserAccountDetail>(`${BASE}/${userId}/`);
  return data;
}

export async function patchUser(userId: number, payload: UserPatchPayload): Promise<UserAccountDetail> {
  const { data } = await apiClient.patch<UserAccountDetail>(`${BASE}/${userId}/`, payload);
  return data;
}

export async function getUserStats(): Promise<UserAccountStats> {
  const { data } = await apiClient.get<UserAccountStats>(`${BASE}/stats/`);
  return data;
}

// Pulls the live "active subscriptions by plan" counts straight from Stripe.
// The backend caches results for ~60s; pass `refresh=true` to force a fresh
// pull (subject to rate limits).
export async function getStripeLiveStats(refresh = false): Promise<StripeLiveStats> {
  const params = refresh ? { refresh: '1' } : undefined;
  const { data } = await apiClient.get<StripeLiveStats>(`${BASE}/stripe-live-stats/`, { params });
  return data;
}

// Rewrites the local Subscription mirror WITHOUT touching Stripe. Use only to
// repair drifted records — to honour a customer cancellation request call
// cancelSubscription instead, or the customer keeps getting billed.
export async function upsertSubscription(userId: number, payload: SubscriptionPayload): Promise<UserSubscription> {
  const { data } = await apiClient.put<UserSubscription>(`${BASE}/${userId}/subscription/`, payload);
  return data;
}

// Deletes the local Subscription record only; does NOT stop Stripe billing.
export async function deleteSubscription(userId: number, reactivateTrial = false): Promise<void> {
  await apiClient.delete(`${BASE}/${userId}/subscription/`, {
    params: reactivateTrial ? { reactivate_trial: true } : undefined,
  });
}

export interface CancelSubscriptionResponse {
  message: string;
  subscription: UserSubscription;
}

// Cancels in Stripe at the end of the paid period, then syncs the local record.
// This is the correct action for "please cancel my subscription".
export async function cancelSubscription(userId: number): Promise<CancelSubscriptionResponse> {
  const { data } = await apiClient.post<CancelSubscriptionResponse>(
    `${BASE}/${userId}/subscription/cancel/`
  );
  return data;
}
